import {
    Box,
    Typography,
    CircularProgress,
    Tooltip,
    IconButton,
    Grid
} from "@mui/material";
import HelpIcon from "@mui/icons-material/HelpOutline";
import MainCard from "../../../components/cards/MainCard";
import AdsClickIcon from '@mui/icons-material/AdsClick';
import GlobalDateRangeSelector from '../../../components/GlobalDateRangeSelector';
import CountUp from 'react-countup';
import { useState, useEffect } from 'react';
import { getDateRangeDisplayText, calculateDateRange } from '../../../utils/dateRangeUtils';
import GlobalAnimatedProgressBar from "../../../components/GlobalAnimatedProgressBar";

const SimulationPerformance = ({
    simulationData,
    simulationDataLoading,
    theme,
    dateRange,
    setDateRange,
    customStartDate,
    setCustomStartDate,
    customEndDate,
    setCustomEndDate
}) => {
    // State to control animation
    const [animateWidth, setAnimateWidth] = useState(false);
    const [animationKey, setAnimationKey] = useState(0);

    const handleDateRangeChange = (event) => {
        // Reset animation immediately when date range changes
        setAnimateWidth(false);
        setAnimationKey(prev => prev + 1);
        setDateRange(event.target.value);
    };

    const handleCustomStartDateChange = (event) => {
        setCustomStartDate(event.target.value);
    };

    const handleCustomEndDateChange = (event) => {
        setCustomEndDate(event.target.value);
    };

    // Reset animation when date range changes
    useEffect(() => {
        setAnimateWidth(false);
        setAnimationKey(prev => prev + 1);
    }, [dateRange, customStartDate, customEndDate]); // Added custom date dependencies

    // Trigger animation when data loads
    useEffect(() => {
        if (simulationData?.length > 0 && !simulationDataLoading) {
            const timer = setTimeout(() => {
                setAnimateWidth(true);
            }, 150);
            return () => clearTimeout(timer);
        }
    }, [simulationData, simulationDataLoading, dateRange, customStartDate, customEndDate]);

    return (
        <MainCard content={false} sx={{ width: "100%", bgcolor: "background.default" }} border={false} boxShadow={true}>
            <Box sx={{ p: 2 }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 2
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}>
                        <AdsClickIcon sx={{ mr: 0.5, fontSize: '1rem' }} color="primary" />
                        Most Clicked Simulations
                    </Typography>

                    <Tooltip title="An overview of the most clicked phishing simulations per simulations sent. Further details can be located under the 'Reports' tab.">
                        <IconButton size="small">
                            <HelpIcon sx={{ color: "secondary.main" }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {/* Date Range Selector Section */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <GlobalDateRangeSelector
                            value={dateRange}
                            onChange={handleDateRangeChange}
                            onCustomStartDateChange={handleCustomStartDateChange}
                            onCustomEndDateChange={handleCustomEndDateChange}
                            customStartDate={customStartDate}
                            customEndDate={customEndDate}
                            includeCustom={true}
                            fullWidth
                            label="Date Range"
                            size="small"
                            showCurrentRange={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Current: {getDateRangeDisplayText(dateRange, customStartDate, customEndDate)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                {/* Loading State */}
                {simulationDataLoading ? (
                    <Box sx={{ height: 455, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
                            Loading simulation data...
                        </Typography>
                    </Box>
                ) : simulationData?.length === 0 ? (
                    /* Empty State */
                    <Box sx={{ height: 455, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="body2" color="textSecondary">
                            No simulation data available for the selected period
                        </Typography>
                    </Box>
                ) : (
                    /* Data Display */
                    <>
                                <Box sx={{ mt: 4, mb: 4 }}>
                                    {simulationData?.map((sim, index) => {
                                        const clickPercentage = (sim.Clicks / sim.TotalSent) * 100;

                                        return (
                                            <Box key={`${sim.SimulationId}-${animationKey}`} sx={{ mb: 3 }}>
                                                <GlobalAnimatedProgressBar
                                                    percentage={clickPercentage}
                                                    label={sim.SimulationName}
                                                    value={sim.Clicks}
                                                    total={sim.TotalSent}
                                                    colorScheme="gradient" 
                                                    showCounts={true}
                                                    showPercentage={true}
                                                    showLabel={true}
                                                    textPosition="right"
                                                    animationDelay={index * 0.15}
                                                    countUpDelay={index * 0.15 + 0.3}
                                                    resetKey={animationKey}
                                                    triggerAnimation={!simulationDataLoading && simulationData?.length > 0}
                                                    size="medium"
                                                    showShineEffect={true}
                                                />
                                            </Box>
                                        );
                                    })}
                                </Box>

                        {/* Top Performer Card */}
                                {simulationData?.length > 0 && (
                                    <Box
                                        key={`top-performer-${animationKey}`}
                                        sx={{
                                            mt: 4,
                                            opacity: animateWidth ? 1 : 0,
                                            transform: animateWidth ? 'translateY(0)' : 'translateY(20px)',
                                            transition: 'all 0.6s ease-out',
                                            transitionDelay: '1s'
                                        }}
                                    >
                                        <MainCard
                                            sx={{
                                                bgcolor: 'primary.main',
                                                border: '1px solid',
                                                borderColor: 'primary.light',
                                                boxShadow: `0 2px 8px ${theme.palette.primary.light}20`,
                                                color: 'primary.contrastText'
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Box>
                                                    <Typography
                                                        variant="subtitle2"
                                                        sx={{
                                                            mb: 0.5,
                                                            fontWeight: 600,
                                                            color: 'inherit'
                                                        }}
                                                    >
                                                        Most clicked simulation:
                                                    </Typography>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="medium"
                                                        sx={{ color: 'inherit' }}
                                                    >
                                                        {simulationData[0]?.SimulationName}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ textAlign: 'right' }}>
                                                    <Typography
                                                        variant="h5"
                                                        fontWeight="bold"
                                                        sx={{
                                                            mb: 0,
                                                            color: 'inherit' 
                                                        }}
                                                    >
                                                        {animateWidth && (
                                                            <CountUp
                                                                key={`top-clicks-${animationKey}`}
                                                                start={0}
                                                                end={simulationData[0]?.Clicks}
                                                                duration={1.5}
                                                                delay={1.2}
                                                                preserveValue
                                                            />
                                                        )}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ color: 'inherit' }}
                                                    >
                                                        out of {simulationData[0]?.TotalSent} (
                                                        {animateWidth && (
                                                            <CountUp
                                                                key={`top-percentage-${animationKey}`}
                                                                start={0}
                                                                end={Math.round((simulationData[0]?.Clicks / simulationData[0]?.TotalSent) * 100)}
                                                                duration={1.5}
                                                                delay={1.2}
                                                                preserveValue
                                                            />
                                                        )}%)
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </MainCard>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </MainCard>
    );
};

export default SimulationPerformance;